/**
 * Theme management — light/dark mode toggle stored in localStorage.
 *
 * The dark class is applied to <html> so CSS custom property
 * overrides in tokens.css take effect globally. On first load,
 * defaults to the user's OS preference via prefers-color-scheme.
 */

const STORAGE_KEY = "mic-theme";

export type Theme = "light" | "dark";

export function getStoredTheme(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "light" || value === "dark") return value;
    return null;
  } catch {
    return null;
  }
}

export function getEffectiveTheme(): Theme {
  const stored = getStoredTheme();
  if (stored !== null) return stored;
  if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // localStorage unavailable — theme still applies for this session
  }
  applyTheme(theme);
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  // Update meta theme-color for mobile browser chrome
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta !== null) {
    meta.setAttribute("content", theme === "dark" ? "#1A1210" : "#FEF2DD");
  }
}

export function toggleTheme(): Theme {
  const current = getEffectiveTheme();
  const next: Theme = current === "dark" ? "light" : "dark";
  setTheme(next);
  return next;
}
